import { useState, useMemo, useCallback } from 'react';
import { Scorecard, ScorecardRow, Text } from '@socrait/react';
import { AttendanceData, SessionStatus } from '~/services/types';

import { getDisplayText, styles as ReviewScorecardStyles } from '../ReviewScoreCards';

type AttendanceScorecardProps = {
  attendanceData: AttendanceData[];
  sessionStatus?: SessionStatus;
};

const getAttendanceTotal = (data?: AttendanceData[]) => {
  if (!data) return undefined;

  return data.reduce((acc, curr) => {
    return acc + curr.data.length;
  }, 0);
};

export const AttendanceScorecard = ({ attendanceData, sessionStatus }: AttendanceScorecardProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const total = useMemo(() => getAttendanceTotal(attendanceData), [attendanceData]);

  const toggleRow = useCallback(
    (rowId: string) => {
      if (expandedRow === rowId) {
        setExpandedRow(null);
      } else {
        setExpandedRow(rowId);
      }
    },
    [expandedRow],
  );

  return (
    <Scorecard title="Attendance signals" variant="subtle">
      {sessionStatus === 'COMPLETE' && !!total ? (
        attendanceData?.map((datum, _index) => {
          const text = datum.title;
          const key = datum.attendanceType;
          const count = datum.data.length;
          const rowData = datum.data.map(({ student }) => {
            return {
              id: student.id,
              name: student.voiceName,
            };
          });

          return (
            <ScorecardRow
              key={`attendance-${key}`}
              rowId={key}
              text={text}
              count={count}
              data={rowData}
              isExpanded={expandedRow === key}
              toggleRow={toggleRow}
            />
          );
        })
      ) : (
        <Text style={ReviewScorecardStyles.noDataText}>
          {getDisplayText('attendance', sessionStatus, attendanceData, total)}
        </Text>
      )}
    </Scorecard>
  );
};
